import React from 'react';
import { IDataverseQuote } from '../../types';
import { IDealersCurrency } from '@/types';
import { formatDealerAmount } from '@/utils/currency';

interface QuoteSummarySectionProps {
  quote: IDataverseQuote;
  dealersCurrency?: IDealersCurrency | null;
  exchangeRate?: number;
}

export const QuoteSummarySection: React.FC<QuoteSummarySectionProps> = ({
  quote,
  dealersCurrency,
  exchangeRate,
}) => {
  const formatAmount = (amount: number) =>
    formatDealerAmount(amount, dealersCurrency, exchangeRate);

  const hasDiscount = (quote.dogma_vssdiscountamount ?? 0) > 0;

  return (
    <div className="bg-white rounded-lg p-6">
      <h3 className="text-lg text-left font-semibold text-gray-900 mb-4">Quote Summary</h3>
      <div className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Subtotal (Gross)</span>
          <span className="font-semibold text-gray-900">{formatAmount(quote.dogma_vsssubtotal ?? 0)}</span>
        </div>

        {hasDiscount && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                Discount Amount
                {quote.dogma_vssdiscountpercentage
                  ? ` (${quote.dogma_vssdiscountpercentage}%)`
                  : ''}
              </span>
              <span className="text-red-500 font-semibold">
                -{formatAmount(quote.dogma_vssdiscountamount ?? 0)}
              </span>
            </div>
            <div className="flex justify-between text-sm font-bold">
              <span className="text-gray-900">After Discount</span>
              <span className="text-gray-900">
                {formatAmount(
                  (quote.dogma_vsssubtotal ?? 0) - (quote.dogma_vssdiscountamount ?? 0)
                )}
              </span>
            </div>
          </>
        )}

        {(quote.freightamount ?? 0) > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Delivery Charge</span>
            <span className="font-semibold text-gray-900">{formatAmount(quote.freightamount ?? 0)}</span>
          </div>
        )}

        <div className="flex justify-between text-sm">
          <span className="text-gray-600">
            VAT ({dealersCurrency?.dogma_vatpercentage ?? 20}%)
          </span>
          <span className="font-semibold text-gray-900">{formatAmount(quote.dogma_vssvatamount ?? 0)}</span>
        </div>

        <div className="h-px bg-gray-200" />

        <div className="flex justify-between text-lg font-bold">
          <span className="text-gray-900">Total Amount</span>
          <span className="text-primary">{formatAmount(quote.dogma_vsstotalamount ?? 0)}</span>
        </div>
      </div>
    </div>
  );
};
